import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  MessageCircle,
  Loader2,
  ExternalLink,
  Copy,
  Check,
  Info,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type {
  APIResponse,
  Campaign,
  CampaignRecipient,
  CampaignRecipientsResponse,
} from '@/types'

interface WhatsAppDispatchModalProps {
  campaign: Campaign | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onLogged?: () => void
}

/**
 * Click-to-send WhatsApp dispatch modal. The manager opens each
 * wa.me link in their own WhatsApp Web (so the recipient sees the
 * restaurant's number as sender, not Bhookly's). Every open writes
 * a clicked_to_send row to outbound_messages so the campaign card
 * still reflects activity.
 *
 * Three actions in the floating row at the bottom:
 *   - Copy all phones — paste into bulk-WhatsApp tools if the
 *     restaurant has one
 *   - Mark all sent — bulk-logs clicked_to_send for every recipient
 *     at once (use when the manager batches via a third-party tool)
 *   - Done — close the modal
 */
export function WhatsAppDispatchModal({
  campaign,
  open,
  onOpenChange,
  onLogged,
}: WhatsAppDispatchModalProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [opened, setOpened] = useState<Set<string>>(new Set())
  const [copied, setCopied] = useState(false)

  const recipientsQuery = useQuery<APIResponse<CampaignRecipientsResponse>>({
    queryKey: ['campaign-recipients', campaign?.id, 'whatsapp_click'],
    queryFn: () => apiClient.getCampaignRecipients(campaign!.id, 'whatsapp_click'),
    enabled: Boolean(campaign) && open,
    staleTime: 30_000,
  })
  const recipients = recipientsQuery.data?.data?.recipients ?? []

  const logSingle = useMutation({
    mutationFn: (customerId: string) =>
      apiClient.logCampaignWhatsAppClick(campaign!.id, [customerId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      onLogged?.()
    },
  })

  const logBulk = useMutation({
    mutationFn: (ids: string[]) => apiClient.logCampaignWhatsAppClick(campaign!.id, ids),
    onSuccess: (res) => {
      const d = res.data
      toast({ title: 'Logged', description: `${d?.logged ?? 0} recipients marked sent.` })
      setOpened((prev) => {
        const next = new Set(prev)
        recipients.forEach((r) => next.add(r.customer_id))
        return next
      })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      onLogged?.()
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Bulk log failed', description: msg, variant: 'destructive' })
    },
  })

  const sendableRecipients = useMemo(
    () => recipients.filter((r) => r.phone_e164 && r.wa_me_url),
    [recipients]
  )

  function openWA(r: CampaignRecipient) {
    if (!r.wa_me_url) return
    window.open(r.wa_me_url, '_blank', 'noopener,noreferrer')
    setOpened((prev) => new Set(prev).add(r.customer_id))
    logSingle.mutate(r.customer_id)
  }

  function copyAllPhones() {
    const phones = sendableRecipients
      .map((r) => r.phone_e164)
      .filter(Boolean)
      .join('\n')
    navigator.clipboard
      .writeText(phones)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
        toast({ title: 'Copied', description: `${sendableRecipients.length} phone numbers copied.` })
      })
      .catch(() => {
        toast({ title: 'Clipboard denied', variant: 'destructive' })
      })
  }

  function markAllSent() {
    if (sendableRecipients.length === 0) return
    logBulk.mutate(sendableRecipients.map((r) => r.customer_id))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="border-b border-border/70 px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MessageCircle className="h-5 w-5" />
            WhatsApp dispatch · {campaign?.name ?? ''}
          </DialogTitle>
          <DialogDescription className="text-sm">
            Click each "Send" to open the recipient's wa.me link in your
            WhatsApp Web — you'll fire each message manually so it
            sends from your number. Every click logs an event for the
            campaign's attribution stats.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4">
          {recipientsQuery.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : recipients.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
              <Info className="h-5 w-5" />
              No recipients in this segment right now.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <strong>Manual send.</strong> Each click opens WhatsApp Web with the
                pre-written message — you press Send there. Status reflects clicks,
                not actual deliveries.
              </div>

              <div className="rounded-md border border-border/80 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Name</TableHead>
                      <TableHead className="w-[22%]">Phone</TableHead>
                      <TableHead className="w-[28%] hidden md:table-cell">Body preview</TableHead>
                      <TableHead className="w-[20%] text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipients.map((r) => {
                      const isOpened = opened.has(r.customer_id) || r.already_sent_in_last_30d
                      const canSend = Boolean(r.phone_e164 && r.wa_me_url)
                      return (
                        <TableRow key={r.customer_id}>
                          <TableCell className="text-sm">
                            <div className="font-medium">
                              {r.display_name || <span className="italic text-muted-foreground">unnamed</span>}
                            </div>
                            {r.already_sent_in_last_30d && (
                              <Badge variant="outline" className="mt-1 text-sm">
                                Sent in last 30d
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {r.phone_e164 || (
                              <span className="text-muted-foreground">— no phone —</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden text-sm text-muted-foreground line-clamp-2 md:table-cell">
                            {r.rendered_body}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              size="sm"
                              variant={isOpened ? 'outline' : 'default'}
                              disabled={!canSend}
                              onClick={() => openWA(r)}
                              className="h-7 gap-1.5 text-sm"
                            >
                              {isOpened ? <Check className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />}
                              {isOpened ? 'Sent' : 'Send'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 bg-muted/30 px-6 py-3">
          <div className="text-sm text-muted-foreground">
            {recipients.length > 0 && (
              <>
                <span className="font-medium text-foreground">{opened.size}</span> of{' '}
                <span className="font-medium text-foreground">{sendableRecipients.length}</span> sent
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyAllPhones}
              disabled={sendableRecipients.length === 0}
              className="gap-1.5"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              Copy all phones
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={markAllSent}
              disabled={sendableRecipients.length === 0 || logBulk.isPending}
              className="gap-1.5"
            >
              {logBulk.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Mark all sent
            </Button>
            <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
